/**
 * Request and response shapes for `BACKLOG.md` A4: viewing and bulk-revoking sessions, for
 * workforce-termination response and self-service "I think my account is compromised".
 *
 * Two surfaces share these shapes:
 *
 *   - `GET /api/auth/sessions` / `POST /api/auth/sessions/revoke-all` — a caller acting on their
 *     own sessions. No capability beyond an active session, since "see and end my own logins" needs
 *     no administrative authority.
 *   - `GET /api/orgs/members/:userId/sessions` / `POST /api/orgs/members/:userId/sessions/revoke-all`
 *     — an admin acting on a specific member's sessions, gated on `org:manage_members`.
 *
 * Same reasoning as `authRequests.ts`: every object schema is strict (an unrecognised key is a
 * rejection), and nothing server-owned is accepted from a client (`claude-kit/rules/invariants.md`,
 * "Server-authoritative values never come from the request") — `includeCurrent` below is the one
 * caller-supplied field on this whole surface, and it is a request about the caller's *own* session,
 * never an identifier for someone else's.
 */

import { z } from 'zod';

const instantSchema = z.iso.datetime({ offset: true });

/**
 * One session, as shown to a viewer.
 *
 * `sourceIp`/`userAgent` are server-resolved facts recorded at session creation
 * (`auth/middleware.ts`'s `resolveRequestMeta`), never anything the client asserts about itself
 * after the fact.
 */
export const sessionListItemSchema = z.strictObject({
  createdAt: instantSchema,
  lastSeenAt: instantSchema,
  sourceIp: z.string().nullable(),
  userAgent: z.string().nullable(),
  /**
   * Does this row match the session the request itself arrived on? Only ever `true` somewhere in
   * the self-service listing (`GET /api/auth/sessions`) — the admin listing of another member's
   * sessions has no "current session" to compare against from the admin's own point of view, so
   * every row there is `false`.
   */
  isCurrent: z.boolean(),
});
export type SessionListItem = z.infer<typeof sessionListItemSchema>;

/** Shared by both `GET` endpoints above. */
export const sessionsResponseSchema = z.strictObject({
  sessions: z.array(sessionListItemSchema),
});
export type SessionsResponse = z.infer<typeof sessionsResponseSchema>;

/**
 * Self-service "log out everywhere" — `POST /api/auth/sessions/revoke-all`.
 *
 * `includeCurrent` decides whether the session the request itself arrived on is revoked too.
 * Defaulting it to `true` would end the very session the caller is using to make the request, with
 * the response arriving over a cookie that no longer works — "log out everywhere including this
 * device" is a real, deliberate action (finishing a compromise response by ending the one session
 * the caller can still see and use), not the ordinary case, so it is opt-in.
 */
export const revokeOwnSessionsRequestSchema = z.strictObject({
  includeCurrent: z.boolean().optional(),
});
export type RevokeOwnSessionsRequest = z.infer<typeof revokeOwnSessionsRequestSchema>;

export const revokeOwnSessionsResponseSchema = z.strictObject({
  revokedCount: z.number().int().nonnegative(),
  /** Whether the caller's own current session was one of the ones revoked. See `includeCurrent`. */
  currentSessionRevoked: z.boolean(),
});
export type RevokeOwnSessionsResponse = z.infer<typeof revokeOwnSessionsResponseSchema>;

/**
 * Admin revoke-all-for-member — `POST /api/orgs/members/:userId/sessions/revoke-all`.
 *
 * No request body, and no `includeCurrent`-equivalent flag: ending every session a terminated
 * member holds *in this practice* is unconditional, which is the point of the workforce-termination
 * use case this exists for. There is also no "current session" concept from the admin's own point of
 * view to make optional in the first place — the admin is never the member being acted on in the
 * ordinary case, and if they are, ending their own access on purpose by naming their own `userId` is
 * not a case this endpoint needs to guard against.
 */
export const revokeMemberSessionsResponseSchema = z.strictObject({
  revokedCount: z.number().int().nonnegative(),
});
export type RevokeMemberSessionsResponse = z.infer<typeof revokeMemberSessionsResponseSchema>;
